import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { C, R, F, btnPrimary, btnSecondary, card, input, pageHeader, sectionLabel, spinner, toastStyle } from '../lib/designTokens'

/**
 * ExecutiveReportConfig.jsx
 * ─────────────────────────────────────────────────────────────────────────
 * Batch A — Infrastructure (Data Control Panel)
 *
 * A dedicated settings page (not a modal) for manually-entered Executive
 * Report content. Deliberately built as a standalone, reusable page since
 * this concept (report-level manual overlays, versioned per reporting
 * period) is expected to grow and be reused by other report types later.
 *
 * IMPORTANT — separation of concerns:
 *   This page NEVER reads or writes any calculated financial value
 *   (Budget, Actual, EAC, VAC, CPI, Cash Position, Packages, etc.). It only
 *   manages rows in `executive_report_configs`, a table fully separate from
 *   project financial data (see migration 002). ExecutiveReport.jsx (Batch B)
 *   is responsible for reading this config and merging it with live
 *   financial data at render time — this file does not touch that merge.
 *
 * Versioning model:
 *   One row per (project_id, reporting_period), mirroring the existing
 *   forecast_history / forecast_active_date pattern already used elsewhere
 *   in the project. Editing a period after it's been saved updates that
 *   period's row; a new period creates a new row. No workflow (draft/
 *   approved) is implemented — the `status` column exists for that future
 *   use only, per the agreed architecture.
 */

const FIELD_GROUPS = [
  {
    title: 'Report Identity',
    fields: [
      { key: 'report_title', label: 'Report Title', type: 'text', placeholder: 'Executive Summary Report' },
      { key: 'report_subtitle', label: 'Report Subtitle', type: 'text', placeholder: 'e.g. Q3 Board Review — Jeddah Oceanarium' },
      { key: 'report_date', label: 'Report Date', type: 'date', placeholder: '' },
    ],
  },
  {
    title: 'Sign-off',
    fields: [
      { key: 'prepared_by', label: 'Prepared By', type: 'text', placeholder: 'e.g. Wessam Taha, Cost Control' },
      { key: 'approved_by', label: 'Approved By', type: 'text', placeholder: 'e.g. Director of Operations' },
    ],
  },
  {
    title: 'Progress (Manual Entry)',
    fields: [
      { key: 'manual_percent_complete', label: '% Complete', type: 'number', placeholder: 'e.g. 32.5', min: 0, max: 100, step: 0.1 },
    ],
  },
  {
    title: 'Actual Reporting Period (Release only)',
    fields: [
      // Not a financial snapshot value — This Period for a Release report
      // is computed at render time by summing buildMonthlyActualCostData()'s
      // existing per-Cost-Code periodTotals across this From→To range (a
      // configurable aggregation window, reusing the same monthly engine
      // Current already uses for Movement — see ExecutiveReport.jsx). This
      // config only stores WHICH months to sum for a Release; it never
      // stores the resulting amount itself.
      { key: 'actual_period_from', label: 'From Month', type: 'month-select', placeholder: '' },
      { key: 'actual_period_to', label: 'To Month', type: 'month-select', placeholder: '' },
    ],
  },
  {
    title: 'Executive Narrative',
    fields: [
      { key: 'executive_summary', label: 'Executive Summary', type: 'textarea', placeholder: 'A short narrative overview of the project this period...' },
      { key: 'project_highlights', label: 'Project Highlights', type: 'textarea', placeholder: 'Key achievements or milestones this period...' },
      { key: 'key_challenges', label: 'Key Challenges', type: 'textarea', placeholder: 'Issues affecting cost, schedule, or scope...' },
      { key: 'recommendations', label: 'Recommendations', type: 'textarea', placeholder: 'Recommended actions for management...' },
      { key: 'additional_notes', label: 'Additional Notes', type: 'textarea', placeholder: 'Anything else worth including on the cover or appendix...' },
    ],
  },
]

const EMPTY_FORM = FIELD_GROUPS.flatMap(g => g.fields).reduce((acc, f) => ({ ...acc, [f.key]: '' }), {})

const monthOptions = () => {
  // Current month plus the previous 11 — first-of-month dates, matching
  // the `reporting_period date` column (always stored as day 01).
  //
  // Fix: previously built the value with `dt.toISOString().slice(0,10)`.
  // toISOString() converts to UTC first, so for any timezone ahead of UTC
  // (e.g. Saudi Arabia, UTC+3), opening this page in the early hours of
  // the 1st of a month shifted the computed date back to the last day of
  // the PREVIOUS month. That mismatched whatever `reporting_period` value
  // was actually saved, so the lookup in load() found no matching row and
  // the form appeared empty. Building the YYYY-MM-DD string manually from
  // local date parts (no UTC conversion) avoids this entirely.
  const out = []
  const d = new Date()
  d.setDate(1)
  for (let i = 0; i < 12; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1)
    const yyyy = dt.getFullYear()
    const mm = String(dt.getMonth() + 1).padStart(2, '0')
    const iso = `${yyyy}-${mm}-01`
    const label = dt.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    out.push({ value: iso, label })
  }
  return out
}

export default function ExecutiveReportConfig({ projectId, projectName, onClose }) {
  const periods = monthOptions()
  const [period, setPeriod] = useState(periods[0].value)
  const [form, setForm] = useState(EMPTY_FORM)
  const [existingId, setExistingId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)

  const load = useCallback(async (pid, per) => {
    setLoading(true)
    const { data } = await supabase
      .from('executive_report_configs')
      .select('*')
      .eq('project_id', pid)
      .eq('reporting_period', per)
      .maybeSingle()
    if (data) {
      setExistingId(data.id)
      setForm({
        report_title: data.report_title || '',
        report_subtitle: data.report_subtitle || '',
        report_date: data.report_date || '',
        prepared_by: data.prepared_by || '',
        approved_by: data.approved_by || '',
        manual_percent_complete: data.manual_percent_complete ?? '',
        actual_period_from: data.actual_period_from || '',
        actual_period_to: data.actual_period_to || '',
        executive_summary: data.executive_summary || '',
        project_highlights: data.project_highlights || '',
        key_challenges: data.key_challenges || '',
        recommendations: data.recommendations || '',
        additional_notes: data.additional_notes || '',
      })
    } else {
      setExistingId(null)
      setForm(EMPTY_FORM)
    }
    setLoading(false)
  }, [])

  useEffect(() => { if (projectId) load(projectId, period) }, [projectId, period, load])

  async function handleSave() {
    setSaving(true)
    const payload = {
      project_id: projectId,
      reporting_period: period,
      report_title: form.report_title || null,
      report_subtitle: form.report_subtitle || null,
      report_date: form.report_date || null,
      prepared_by: form.prepared_by || null,
      approved_by: form.approved_by || null,
      manual_percent_complete: form.manual_percent_complete === '' ? null : parseFloat(form.manual_percent_complete),
      actual_period_from: form.actual_period_from || null,
      actual_period_to: form.actual_period_to || null,
      executive_summary: form.executive_summary || null,
      project_highlights: form.project_highlights || null,
      key_challenges: form.key_challenges || null,
      recommendations: form.recommendations || null,
      additional_notes: form.additional_notes || null,
      updated_at: new Date().toISOString(),
    }
    // Fix: previously used a manual insert-vs-update branch keyed off
    // `existingId`, which is only known once the initial load() completes.
    // If handleSave ran before that load resolved (e.g. the user saved
    // quickly on a fresh page open, or a duplicate click raced the first
    // save), `existingId` was still null and this called insert() a second
    // time for the same (project_id, reporting_period) — which the table's
    // UNIQUE constraint silently rejected, so the save appeared to succeed
    // in the UI (no error was surfaced clearly) but nothing was actually
    // persisted. upsert() with onConflict on the same unique key removes
    // this race entirely: it always writes the one correct row regardless
    // of whether existingId had loaded yet.
    const { data, error } = await supabase
      .from('executive_report_configs')
      .upsert(payload, { onConflict: 'project_id,reporting_period' })
      .select()
      .maybeSingle()
    setSaving(false)
    if (error) {
      setToast({ type: 'error', message: 'Failed to save: ' + error.message })
    } else {
      if (data?.id) setExistingId(data.id)
      setToast({ type: 'success', message: 'Report configuration saved' })
      load(projectId, period)
    }
  }

  const setField = (key, val) => setForm(f => ({ ...f, [key]: val }))

  return (
    <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 20px' }}>
      <div style={pageHeader}>
        <div>
          <h1 style={{ fontSize: F.h1, fontWeight: 700, color: C.ink, margin: 0 }}>Report Configuration</h1>
          <div style={{ fontSize: F.body, color: C.ink3, marginTop: 4 }}>
            {projectName} — Executive Report manual content
          </div>
        </div>
        {onClose && (
          <button style={btnSecondary} onClick={onClose}>Close</button>
        )}
      </div>

      <div style={{ ...card, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{ fontSize: F.body, fontWeight: 600, color: C.ink2 }}>Reporting Period</label>
        <select style={{ ...input, width: 220 }} value={period} onChange={e => setPeriod(e.target.value)}>
          {periods.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {existingId && <span style={{ fontSize: F.caption, color: C.ink3 }}>Editing existing configuration for this period</span>}
        {!existingId && !loading && <span style={{ fontSize: F.caption, color: C.ink3 }}>No configuration yet for this period — fields will be created on save</span>}
      </div>

      {loading ? (
        <div style={spinner} />
      ) : (
        <>
          {FIELD_GROUPS.map(group => (
            <div key={group.title} style={{ ...card, marginBottom: 20 }}>
              <div style={sectionLabel}>{group.title}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 12 }}>
                {group.fields.map(f => (
                  <div key={f.key}>
                    <label style={{ display: 'block', fontSize: F.caption, fontWeight: 600, color: C.ink2, marginBottom: 6 }}>
                      {f.label} <span style={{ fontWeight: 400, color: C.ink3 }}>(optional)</span>
                    </label>
                    {f.type === 'textarea' ? (
                      <textarea
                        style={{ ...input, minHeight: 90, resize: 'vertical', fontFamily: 'inherit' }}
                        value={form[f.key]}
                        placeholder={f.placeholder}
                        onChange={e => setField(f.key, e.target.value)}
                      />
                    ) : f.type === 'month-select' ? (
                      <select style={input} value={form[f.key]} onChange={e => setField(f.key, e.target.value)}>
                        <option value="">— Not set —</option>
                        {periods.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                      </select>
                    ) : (
                      <input
                        style={input}
                        type={f.type}
                        min={f.min}
                        max={f.max}
                        step={f.step}
                        value={form[f.key]}
                        placeholder={f.placeholder}
                        onChange={e => setField(f.key, e.target.value)}
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button style={btnPrimary} onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>
          </div>
        </>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, ...toastStyle(toast.type) }}>
          {toast.message}
          <button onClick={() => setToast(null)} style={{ marginLeft: 12, background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
        </div>
      )}
    </div>
  )
}
